"use client";

import {
  Localizations,
  SupportedLangList,
  useLocalization,
} from "@/helpers/useLocalization";
import { useEffect, useState } from "react";
import {
  europeanLanderLocalizations,
  europeanLanderSlugs,
} from "./localizations";

const originalTexts = { ...europeanLanderLocalizations[Localizations.English] };

const getText = (slug: europeanLanderSlugs, locale: SupportedLangList) => {
  const localizationTexts = europeanLanderLocalizations[locale];
  return localizationTexts[slug];
};

/**
 * Same state/effect dance the older strings below do by hand, packaged up so keys added
 * from here on cost one line instead of twelve. Also lets callers that already hold a
 * localization key (the product group headings) resolve it without a named field.
 */
export const useText = (slug: europeanLanderSlugs) => {
  const { localization } = useLocalization();
  const [text, setText] = useState(originalTexts[slug]);

  useEffect(() => {
    setText(getText(slug, localization as SupportedLangList));
  }, [slug, localization]);

  return text;
};

export const useTranslations = () => {
  const { localization } = useLocalization();

  const ezclickGlassDescription = useText("ezclick-glass-description");
  const activeRootXlDescription = useText("active-root-xl-description");
  const activeSproutDescription = useText("active-sprout-description");

  // hero.title
  const [heroHeadline, setHeroTitle] = useState(originalTexts["hero.title"]);
  useEffect(() => {
    if (localization === Localizations.English) {
      setHeroTitle(originalTexts["hero.title"]);
    }
    setHeroTitle(
      getText(
        "hero.title" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // hero.subtitle
  const [heroSubtitle, setHeroSubtitle] = useState(
    originalTexts["hero.subtitle"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setHeroSubtitle(originalTexts["hero.subtitle"]);
    }
    setHeroSubtitle(
      getText(
        "hero.subtitle" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // hero.get-samples-btn
  const [heroGetSamplesBtn, setHeroGetSamplesBtn] = useState(
    originalTexts["hero.get-samples-btn"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setHeroGetSamplesBtn(originalTexts["hero.get-samples-btn"]);
    }
    setHeroGetSamplesBtn(
      getText(
        "hero.get-samples-btn" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // cerfications.headline
  const [certificationsHeadline, setCertificationsHeadline] = useState(
    originalTexts["certifications.headline"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setCertificationsHeadline(originalTexts["certifications.headline"]);
    } else {
      setCertificationsHeadline(
        getText(
          "certifications.headline" as europeanLanderSlugs,
          localization as SupportedLangList
        )
      );
    }
  }, [localization, setCertificationsHeadline]);

  // cerfications.body
  const [certificationsBody, setCertificationsBody] = useState(
    originalTexts["certifications.body"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setCertificationsBody(originalTexts["certifications.body"]);
    } else {
      setCertificationsBody(
        getText(
          "certifications.body" as europeanLanderSlugs,
          localization as SupportedLangList
        )
      );
    }
  }, [localization]);
  // process-certifications
  const [processCertifications, setProcessCertifications] = useState(
    originalTexts["process-certifications"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setProcessCertifications(originalTexts["process-certifications"]);
    }
    setProcessCertifications(
      getText(
        "process-certifications" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // battery-safety
  const [batterySafety, setBatterySafety] = useState(
    originalTexts["battery-safety"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setBatterySafety(originalTexts["battery-safety"]);
    }
    setBatterySafety(
      getText(
        "battery-safety" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // explore-out-lineup-stocked-in-the-eu
  const [exploreOutLineupStockedInTheEu, setExploreOutLineup] = useState(
    originalTexts["explore-out-lineup-stocked-in-the-eu"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setExploreOutLineup(
        originalTexts["explore-out-lineup-stocked-in-the-eu"]
      );
    }
    setExploreOutLineup(
      getText(
        "explore-out-lineup-stocked-in-the-eu" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // active-axil-mini-air-description
  const [activeAxilMiniAirDescription, setActiveAxilMiniAirDesc] = useState(
    originalTexts["active-axil-mini-air-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setActiveAxilMiniAirDesc(
        originalTexts["active-axil-mini-air-description"]
      );
    }
    setActiveAxilMiniAirDesc(
      getText(
        "active-axil-mini-air-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // active-axil-mini-pro-description
  const [activeAxilMiniProDescription, setActiveAxilMiniProDesc] = useState(
    originalTexts["active-axil-mini-pro-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setActiveAxilMiniProDesc(
        originalTexts["active-axil-mini-pro-description"]
      );
    }
    setActiveAxilMiniProDesc(
      getText(
        "active-axil-mini-pro-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // active-axil-description
  const [activeAxilDescription, setActiveAxilDesc] = useState(
    originalTexts["active-axil-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setActiveAxilDesc(originalTexts["active-axil-description"]);
    }
    setActiveAxilDesc(
      getText(
        "active-axil-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // active-leaf-description
  const [activeLeafDescription, setActiveLeafDesc] = useState(
    originalTexts["active-leaf-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setActiveLeafDesc(originalTexts["active-leaf-description"]);
    }
    setActiveLeafDesc(
      getText(
        "active-leaf-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // active-ceramic-description
  const [activeCeramicDescription, setActiveCeramicDesc] = useState(
    originalTexts["active-ceramic-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setActiveCeramicDesc(originalTexts["active-ceramic-description"]);
    }
    setActiveCeramicDesc(
      getText(
        "active-ceramic-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // ezclick-ceramic-pro-description
  const [ezclickCeramicProDescription, setEzclickCeramicProDesc] = useState(
    originalTexts["ezclick-ceramic-pro-description"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setEzclickCeramicProDesc(
        originalTexts["ezclick-ceramic-pro-description"]
      );
    }
    setEzclickCeramicProDesc(
      getText(
        "ezclick-ceramic-pro-description" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // available-mouthpieces
  const [availableMouthpieces, setAvailableMouthpieces] = useState(
    originalTexts["available-mouthpieces"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setAvailableMouthpieces(originalTexts["available-mouthpieces"]);
    }
    setAvailableMouthpieces(
      getText(
        "available-mouthpieces" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // singular.headline
  const [singularHeadline, setSingularHeadline] = useState(
    originalTexts["singular.headline"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setSingularHeadline(originalTexts["singular.headline"]);
    }
    setSingularHeadline(
      getText(
        "singular.headline" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // singular.body
  const [singularBody, setSingularBody] = useState(
    originalTexts["singular.body"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setSingularBody(originalTexts["singular.body"]);
    }
    setSingularBody(
      getText(
        "singular.body" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // view-link
  const [viewLink, setViewLink] = useState(originalTexts["view-link"]);
  useEffect(() => {
    if (localization === Localizations.English) {
      setViewLink(originalTexts["view-link"]);
    }
    setViewLink(
      getText(
        "view-link" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // products-available-from-our-global-facilities
  const [productsAvailableFromOurGlobalFacilities, setProductsAvailable] =
    useState(originalTexts["products-available-from-our-global-facilities"]);
  useEffect(() => {
    if (localization === Localizations.English) {
      setProductsAvailable(
        originalTexts["products-available-from-our-global-facilities"]
      );
    }
    setProductsAvailable(
      getText(
        "products-available-from-our-global-facilities" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // trusted-by-top-brands
  const [trustedByTopBrands, setTrustedByTopBrands] = useState(
    originalTexts["trusted-by-top-brands"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setTrustedByTopBrands(originalTexts["trusted-by-top-brands"]);
    }
    setTrustedByTopBrands(
      getText(
        "trusted-by-top-brands" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // customizations.headline
  const [customizationsHeadline, setCustomizationsHeadline] = useState(
    originalTexts["customizations.headline"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setCustomizationsHeadline(originalTexts["customizations.headline"]);
    }
    setCustomizationsHeadline(
      getText(
        "customizations.headline" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // customizations.body
  const [customizationsBody, setCustomizationsBody] = useState(
    originalTexts["customizations.body"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setCustomizationsBody(originalTexts["customizations.body"]);
    }
    setCustomizationsBody(
      getText(
        "customizations.body" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  // customizations.button
  const [customizationsButton, setCustomizationsButton] = useState(
    originalTexts["customizations.button"]
  );
  useEffect(() => {
    if (localization === Localizations.English) {
      setCustomizationsButton(originalTexts["customizations.button"]);
    }
    setCustomizationsButton(
      getText(
        "customizations.button" as europeanLanderSlugs,
        localization as SupportedLangList
      )
    );
  }, [localization]);

  return {
    heroHeadline,
    heroSubtitle,
    heroGetSamplesBtn,
    certificationsHeadline,
    certificationsBody,
    processCertifications,
    batterySafety,
    exploreOutLineupStockedInTheEu,
    activeAxilMiniAirDescription,
    activeAxilMiniProDescription,
    activeAxilDescription,
    activeLeafDescription,
    activeCeramicDescription,
    ezclickCeramicProDescription,
    ezclickGlassDescription,
    activeRootXlDescription,
    activeSproutDescription,
    availableMouthpieces,
    singularHeadline,
    singularBody,
    viewLink,
    productsAvailableFromOurGlobalFacilities,
    trustedByTopBrands,
    customizationsBody,
    customizationsButton,
    customizationsHeadline,
  };
};
